import { requiresSingPlus } from "~/lib/episodeAccessGuard";
import { getSuggestedEpisodeIndexSorted } from "~/lib/episodeSuggestion";
import { SERIES_CARD_IMAGE_ORDER, selectPreferredImageUrl } from "~/lib/imageFallback";
import { formatScriptureReference } from "~/lib/scriptureReference";
import { normalizeSeriesOwnershipType } from "~/lib/seriesRoute.utils";
import type {
  GetAllDevotionalSeriesForDashboardResult,
  GetAllEpisodesMinimalBySeriesSlugQueryResult,
  ScriptureReference,
} from "~/sanity/sanity.types";
import type { SeriesProgressResponse } from "~/services/progress.client";
import { ensureEpisodesMinimal, ensureLatestEpisode } from "~/stores/reactQuery/useContentQuery";

type SeriesImages = Array<{
  imageType?: string | null;
  imageName?: string | null;
  imageUrl?: string | null;
}> | null;

/**
 * Extracts the best available artwork URL from a series' images array.
 * Priority order: Feature > Thumbnail > Hero > first available
 */
export function getSeriesArtworkUrl(seriesImages: SeriesImages): string | null {
  return selectPreferredImageUrl(seriesImages, SERIES_CARD_IMAGE_ORDER);
}

export type EpisodeCard = {
  episodeId: string;
  episodeTitle: string;
  episodeSubtitle?: string;
  episodeScriptureReferences: string;
  episodeProgressPercentage: number;
  episodeReleaseDate?: string;
  episodeSlug?: string;
  isSingPlusContent?: boolean;
  episodeCompletedAtUtc?: string;
  episodeLastListenedUtc?: string;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: slug and ownership preference handling is still easiest to follow inline.
export function resolvePinnedSeriesIds(
  series: GetAllDevotionalSeriesForDashboardResult,
  pinnedSlugs?: Array<string>,
): Array<string> {
  const slugToSeries = new Map<string, { id: string; ownershipType: "internal" | "external" }>();
  for (const s of series) {
    if (!s.slug) continue;

    const slug = String(s.slug).toLowerCase();
    const ownershipType = normalizeSeriesOwnershipType(s.ownershipType);
    const existing = slugToSeries.get(slug);

    if (!existing || (existing.ownershipType === "external" && ownershipType === "internal")) {
      slugToSeries.set(slug, { id: s._id, ownershipType });
    }
  }

  const seriesIdSet = new Set(series.map((s) => s._id));
  const slugs = Array.isArray(pinnedSlugs) ? pinnedSlugs : [];
  const pinnedSeriesIdsFromSlugs: Array<string> = [];

  for (const slug of slugs) {
    // Support both CMS IDs and slugs (case-insensitive for slugs)
    if (seriesIdSet.has(slug)) {
      pinnedSeriesIdsFromSlugs.push(slug);
      continue;
    }
    const mapped = slugToSeries.get(String(slug).toLowerCase());
    if (mapped) pinnedSeriesIdsFromSlugs.push(mapped.id);
  }

  // If no pinned series ids, default to 'daily' series if it's available
  let pinnedSeriesIds: Array<string> = pinnedSeriesIdsFromSlugs;
  if (pinnedSeriesIds.length === 0) {
    const dailySeries = slugToSeries.get("daily");
    pinnedSeriesIds = dailySeries ? [dailySeries.id] : series.length > 0 ? [series[0]._id] : [];
  }

  return pinnedSeriesIds;
}

export function getOrderPinnedEpisodeAtFirst(
  series: GetAllDevotionalSeriesForDashboardResult,
  pinnedSeriesIds?: Array<string>,
) {
  if (!Array.isArray(pinnedSeriesIds) || pinnedSeriesIds.length === 0) {
    return series;
  }

  const pinnedIdSet = new Set(pinnedSeriesIds);

  // Maintain original order within each group.
  const pinned = series.filter((s) => pinnedIdSet.has(s._id));
  const nonPinned = series.filter((s) => !pinnedIdSet.has(s._id));

  // Order pinned series by the order of pinnedSeriesIds
  const pinnedByPriority = [...pinned].sort((a, b) => {
    const ai = pinnedSeriesIds.indexOf(a._id);
    const bi = pinnedSeriesIds.indexOf(b._id);
    return ai - bi;
  });

  return [...pinnedByPriority, ...nonPinned];
}

export function selectMostRecentEpisodeListened<T extends { lastListenedUtc?: string }>(items: Array<T>): T | null {
  if (!items.length) return null;
  return items.reduce((latest, current) => {
    const latestDate = new Date(latest.lastListenedUtc || 0).getTime();
    const currentDate = new Date(current.lastListenedUtc || 0).getTime();
    return currentDate > latestDate ? current : latest;
  });
}

export function formatScriptureReferences(
  refs:
    | Array<{
        book?: string | null;
        startChapter?: number | null;
        startVerse?: number | null;
        endChapter?: number | null;
        endVerse?: number | null;
      }>
    | undefined,
): string {
  if (!Array.isArray(refs) || refs.length === 0) return "";
  const parts = refs
    .map((r) =>
      formatScriptureReference({
        book: (r.book ?? undefined) as ScriptureReference["book"] | undefined,
        startChapter: r.startChapter ?? undefined,
        startVerse: r.startVerse ?? undefined,
        endChapter: r.endChapter ?? undefined,
        endVerse: r.endVerse ?? undefined,
      }),
    )
    .filter((s) => Boolean(s));
  return parts.join(", ");
}

export async function getEpisodeCardForOngoing(
  seriesId: string,
  progress: SeriesProgressResponse,
  freePeriod?: number | null,
  hasDevoPlus?: boolean,
): Promise<EpisodeCard | null> {
  const latest = await ensureLatestEpisode(seriesId);
  if (!latest) return null;

  const epProgress = progress.devotionals.find((d) => d.cmsId === latest._id);
  const episodeProgressPercentage = epProgress?.progressPercentage ?? 0;
  const isCompleted = Boolean(epProgress?.completedAtUtc);

  const isSingPlusContent = requiresSingPlus({
    permissions: latest.permissions,
    releaseDate: latest.releaseDate,
    hasDevoPlus: hasDevoPlus ?? false,
    freePeriod,
  });

  return {
    episodeId: latest._id,
    episodeTitle: latest.name ?? "",
    episodeSubtitle: latest.subtitle ?? undefined,
    episodeScriptureReferences: formatScriptureReferences(latest.scriptureReferences ?? []),
    episodeProgressPercentage: isCompleted ? 100 : episodeProgressPercentage,
    episodeReleaseDate: latest.releaseDate ?? "",
    episodeSlug: latest.slug?.current ?? undefined,
    episodeCompletedAtUtc: epProgress?.completedAtUtc,
    isSingPlusContent,
  };
}

type ProgressItem = SeriesProgressResponse["devotionals"][number];
type Episode = GetAllEpisodesMinimalBySeriesSlugQueryResult[number];

/**
 * Builds an EpisodeCard from an episode and optional progress data.
 * Centralizes the mapping logic to avoid duplication.
 */
function buildEpisodeCard(args: {
  episode: Episode;
  progress?: ProgressItem;
  freePeriod?: number | null;
  hasDevoPlus: boolean;
}): EpisodeCard {
  const { episode, progress, freePeriod, hasDevoPlus } = args;

  const isSingPlusContent = requiresSingPlus({
    permissions: episode.permissions,
    releaseDate: episode.releaseDate,
    hasDevoPlus,
    freePeriod,
  });

  return {
    episodeId: episode._id,
    episodeTitle: episode.title ?? "",
    episodeSubtitle: episode.subtitle ?? undefined,
    episodeScriptureReferences: formatScriptureReferences(episode.scriptureReferences ?? []),
    episodeProgressPercentage: progress?.progressPercentage ?? 0,
    episodeReleaseDate: episode.releaseDate ?? "",
    episodeCompletedAtUtc: progress?.completedAtUtc,
    episodeSlug: episode.slug?.current ?? undefined,
    isSingPlusContent,
  };
}

function getEpisodeCardForFixed(
  progress: SeriesProgressResponse,
  episodes: GetAllEpisodesMinimalBySeriesSlugQueryResult,
  freePeriod?: number | null,
  hasDevoPlus?: boolean,
): EpisodeCard | null {
  if (episodes.length === 0) return null;

  const devotionals = progress.devotionals;
  const hasPlus = hasDevoPlus ?? false;

  // Build progress lookup map
  const progressById = new Map<string, ProgressItem>(devotionals.map((d) => [d.cmsId, d]));

  // Use shared suggestion logic
  const { sortedEpisodes, suggestedIndex } = getSuggestedEpisodeIndexSorted({
    episodes,
    getEpisodeId: (ep) => ep._id,
    getReleaseDate: (ep) => ep.releaseDate,
    getProgress: (id) => {
      const p = progressById.get(id);
      if (!p) return undefined;
      return {
        progressPercentage: p.progressPercentage,
        lastListenedUtc: p.lastListenedUtc,
        isCompleted: Boolean(p.completedAtUtc),
      };
    },
  });

  const suggestedEpisode = sortedEpisodes[suggestedIndex];
  return buildEpisodeCard({
    episode: suggestedEpisode,
    progress: progressById.get(suggestedEpisode._id),
    freePeriod,
    hasDevoPlus: hasPlus,
  });
}

type SeriesWithOptionalProgress = {
  _id: string;
  seriesType?: string | null;
  progress?: SeriesProgressResponse | null;
  slug?: string | null;
  ownershipType?: string | null;
  freePeriod?: number | null;
};

export async function resolveEpisodeCardForSeries(
  series: SeriesWithOptionalProgress,
  hasDevoPlus?: boolean,
): Promise<EpisodeCard | null> {
  const seriesId = series._id;
  const progress = series.progress ?? ({ seriesId: seriesId, devotionals: [] } as SeriesProgressResponse);

  if (series.seriesType === "ongoing") {
    return getEpisodeCardForOngoing(seriesId, progress, series.freePeriod, hasDevoPlus);
  }

  // Get all episodes from the series
  const episodes = await ensureEpisodesMinimal(series.slug ?? "", series.ownershipType);

  // default to fixed behavior
  return getEpisodeCardForFixed(progress, episodes, series.freePeriod, hasDevoPlus);
}
